import React, { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import ReactPlayer from 'react-player'
import styled from 'styled-components'
import { translateErrors } from '../../globalStates/utils'
import { useLanguageState } from '../../globalStates/LanguageState'
import { mapErrorsToString } from '../../utils/Errors'
import Button from '../Button'
import LoadingIndicator from '../LoadingIndicator';

const FilePickerWrapper = styled.div`
    display: flex;
    flex-direction: column;
    & > label:first-child {
        font-weight: 700;
    }
    & > label {
        margin-bottom: 25px;
    }
`

const VideoPickerWrapper = styled.div`
    display: flex;
    flex-direction: row;
    margin-left: -5px;
    margin-right: -5px;
    & > * {
        width: calc(50% - 10px);
        min-height: 200px;
        min-width: 200px;
        max-height: 200px;
        margin: 0 5px 20px 5px;
    }
`

const StyledDropzone = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    background-repeat: no-repeat;
    background-position: center;
    background-color: #C4C4C4;
    border: 2px dashed #c9c9c9;
    border-radius: 5px;
`

interface VideoPlayerProps {
    source?: string
}

const VideoPlayerWrapper = styled.div<VideoPlayerProps>`
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: ${props => props.source ? '#FFF' : '#C4C4C4'};
`

const ButtonsBar = styled.div`
    margin-left: -5px;
    margin-right: -5px;
    & > * {
        width: calc(50% - 10px);
        min-width: 200px;
        margin: 5px;
    }
`

const UploadMessage = styled.div`
    text-align: center;
    color: #FF0000;
`

const PlaceholderTitle = styled.div`
    font-weight: 500;
    font-size: 16px;
    line-height: 19px;
    text-align: center;
    color: #727272;
    margin-bottom: 7.5px;
`

const PlaceholderText = styled.div`
    font-weight: normal;
    font-size: 12px;
    line-height: 18px;
    text-align: center;
    color: #727272;
    margin-top: 7.5px;
    padding: 0 40px;
`


interface FilePickerProps {
    name: string
    label: string
    placeholderTitle: string
    placeholderText: string
    value?: string
    onChangeVideo: (image: File, onUploadProgress: (progress: number) => void, onSuccessCallback: () => void, onErrorCallback: (reason: string) => void) => void
    onRemoveVideo: () => void
    img?: string
}

const VideoPicker: React.FC<FilePickerProps> = ({ name, label, placeholderTitle, placeholderText, value, onChangeVideo, onRemoveVideo }) => {
    const branding = useLanguageState().getStrings()
    const [uploading, setUploading] = useState(false)
    const [error, setError] = useState<string | undefined>(undefined)
    const [progress, setProgress] = useState(0)

    const onUploadProgress = useCallback((progress: number) => {
        setProgress(progress)
    }, [])
    const onSuccessCallback = useCallback(() => {
        setUploading(false)
        setError(undefined)
    }, [])
    const onErrorCallback = useCallback((reason: string) => {
        setUploading(false)
        setError(mapErrorsToString(reason))
    }, [])
    const onDrop = useCallback((images: File[]) => {
        setError(undefined)
        setUploading(true)
        const image = images && images[0]
        if (image) {
            onChangeVideo(image, onUploadProgress, onSuccessCallback, onErrorCallback)
        } else {
            onErrorCallback('invalidFileType')
        }
    }, [onChangeVideo, onErrorCallback, onSuccessCallback, onUploadProgress])

    const onRemove = useCallback(() => {
        setUploading(false)
        setError(undefined)
        onRemoveVideo()
    }, [onRemoveVideo])

    const { getRootProps, getInputProps, open } = useDropzone({ onDrop, accept: 'video/mp4', multiple: false })

    return (
        <FilePickerWrapper>
            <label>{label}</label>
            <VideoPickerWrapper>
                <StyledDropzone {...getRootProps({ refKey: 'innerRef' })} className={error ? "invalid" : ""} id={name}>
                    <input {...getInputProps()} />
                    {error && <UploadMessage>{translateErrors(error, branding)}</UploadMessage>}
                    {!error && <PlaceholderTitle>{placeholderTitle}</PlaceholderTitle>}
                    <PlaceholderText>{placeholderText}</PlaceholderText>
                    {uploading && <LoadingIndicator progress={progress} />}
                </StyledDropzone>
                <VideoPlayerWrapper source={value}>
                    <ReactPlayer url={value} controls width="100%" height="100%" />
                </VideoPlayerWrapper>
            </VideoPickerWrapper>
            <ButtonsBar>
                <Button caption={branding.configuration.profilePage.sctVideo.btnBrowseFiles} inverse onClick={open} />
                <Button caption={branding.configuration.profilePage.sctVideo.btnDeleteVideo} inverse={false} onClick={onRemove} />
            </ButtonsBar>
        </FilePickerWrapper>
    )
}

export default VideoPicker